<div class="modal-wrapper" (mousedown)="close()">
  <div class="modal-contents wide" (mousedown)="stopPropagation($event)">
    <div class="modal-header">
      <div class="modal-title">{{stepTitle()}}</div>
      <!-- Print the Min/Max table (same icon as the transfer header's print button; Ctrl/Cmd+P does the same). -->
      <div *ngIf="step === 'replenishPreview'" class="header-actions">
        <div class="export-button print" title="Print" (click)="printSuggestions()">
          <svg width="100%" height="100%" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M448 1536h896v-256h-896v256zm0-640h896v-384h-160q-40 0-68-28t-28-68v-160h-640v640zm1152 64q0-26-19-45t-45-19-45 19-19 45 19 45 45 19 45-19 19-45zm128 0v416q0 13-9.5 22.5t-22.5 9.5h-224v160q0 40-28 68t-68 28h-960q-40 0-68-28t-28-68v-160h-224q-13 0-22.5-9.5t-9.5-22.5v-416q0-79 56.5-135.5t135.5-56.5h64v-544q0-40 28-68t68-28h672q40 0 88 20t76 48l152 152q28 28 48 76t20 88v256h64q79 0 135.5 56.5t56.5 135.5z"/></svg>
        </div>
      </div>
    </div>

    <div class="modal-body" [ngSwitch]="step">

      <!-- ==== Mode selection ==== -->
      <div *ngSwitchCase="'mode'" class="mode-tiles">
        <div *ngIf="canImport()" class="mode-tile" (click)="selectMode('import')">
          <div class="tile-name">Import Data</div>
          <div class="tile-description">Upload a CSV or Excel file. Map its columns once — the format is remembered for next time.</div>
        </div>
        <div *ngIf="canReplenish()" class="mode-tile" (click)="selectMode('replenish')">
          <div class="tile-name">Replenish (Min/Max)</div>
          <div class="tile-description">Add items and quantities from stock targets or sales-based days of supply at the target location.</div>
        </div>
        <div *ngIf="canFilter()" class="mode-tile" (click)="selectMode('filter')">
          <div class="tile-name">Add by Filter</div>
          <div class="tile-description">Add all matching inventory, optionally limited to a category or group.</div>
        </div>
      </div>

      <!-- ==== Import: upload ==== -->
      <div *ngSwitchCase="'upload'" class="upload-step">
        <div class="msg">Select a CSV or Excel (.xlsx) file. Recognized formats skip straight to the preview.</div>
        <label class="button green file-button">
          Select File
          <input type="file" class="invisible-input" accept=".csv,.xlsx,.xls,text/csv" (change)="onFileChange($event)" />
        </label>
      </div>

      <!-- ==== Import: mapping ==== -->
      <div *ngSwitchCase="'mapping'" class="mapping-step">
        <div class="step-hint">
          {{rowCount | rf:'number'}} rows. Assign a role to each column — at least one <b>Match On</b> column and a <b>Quantity</b> column are required.
        </div>
        <div class="table-scroll">
          <table class="mapping-table">
            <thead>
              <tr><th>Column</th><th>Sample values</th><th>Role</th><th></th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let header of headers">
                <td class="header-name">{{header}}</td>
                <td class="samples"><span *ngFor="let value of sampleValues(header)" class="sample">{{value}}</span></td>
                <td>
                  <select [ngModel]="headerRole(header)" (ngModelChange)="setRole(header, $event)">
                    <option value="ignore">Ignore</option>
                    <option value="identity">Match on</option>
                    <option value="qty">Quantity</option>
                    <option value="cost">Cost</option>
                  </select>
                </td>
                <td class="order-controls">
                  <ng-container *ngIf="headerRole(header) === 'identity' && mapping.identityColumns.length > 1">
                    <span class="order-index">#{{mapping.identityColumns.indexOf(header) + 1}}</span>
                    <span class="order-button" (click)="moveIdentity(header, -1)">&#9650;</span>
                    <span class="order-button" (click)="moveIdentity(header, 1)">&#9660;</span>
                  </ng-container>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mapping-options">
          <div class="setting-inline">
            <div class="label">Quantity means</div>
            <select [(ngModel)]="mapping.qtySemantics">
              <option value="add">Add this many</option>
              <option value="target">Set count to this (difference is added)</option>
            </select>
          </div>
          <div *ngIf="mapping.costColumn" class="setting-inline">
            <div class="label">Cost is per</div>
            <select [(ngModel)]="mapping.costBasis">
              <option value="unit">Unit</option>
              <option value="case">Case</option>
            </select>
          </div>
        </div>
      </div>

      <!-- ==== Import: preview ==== -->
      <div *ngSwitchCase="'preview'" class="preview-step">
        <div *ngIf="format" class="format-banner">
          Mapped as: <b>{{format.name}}</b>
          <span *ngIf="!isVendorInvoice()" class="edit-link" (click)="editMapping()">(edit)</span>
        </div>

        <div class="summary-chips">
          <div class="chip">{{rowCount | rf:'number'}} rows</div>
          <div class="chip good">{{preview?.matchedCount | rf:'number'}} matched</div>
          <div class="chip" [class.warn]="preview?.unmatchedCount > 0">{{preview?.unmatchedCount | rf:'number'}} unmatched</div>
          <div *ngIf="preview?.repriceCount > 0" class="chip warn">{{preview?.repriceCount}} price changes<ng-container *ngIf="suspectPriceChanges().length > 0"> ({{suspectPriceChanges().length}} need review)</ng-container></div>
          <div *ngIf="preview?.warnings?.length > 0" class="chip warn">{{preview?.warnings?.length}} warnings</div>
        </div>

        <div class="table-scroll">
          <table class="preview-table">
            <thead><tr><th>Item</th><th class="num">Qty</th><th class="num">Cost</th><th>Action</th></tr></thead>
            <tbody>
              <tr *ngFor="let line of preview?.lines">
                <td>{{line.name}}</td>
                <td class="num">{{line.qty | rf:'number'}}</td>
                <td class="num">{{line.cost | rf:'currency'}}</td>
                <td>{{line.action}}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div *ngIf="preview?.priceChanges?.length > 0" class="price-changes-block">
          <div class="block-title">Vendor product price changes</div>
          <div *ngIf="suspectPriceChanges().length > 0" class="guard-note">
            {{suspectPriceChanges().length}} of these move a price by more than 3× — usually a different price basis in the file
            (per unit vs per case). Unless you check them, the vendor product keeps its current price and the line is costed at that price.
          </div>
          <div *ngFor="let change of preview.priceChanges | slice:0:40" class="price-change-row" [class.suspect]="change.suspect">
            <label *ngIf="change.suspect"><input type="checkbox" [(ngModel)]="change.confirm" /></label>
            <span class="name">{{change.name}}</span>
            <span class="prices">{{change.oldPrice | rf:'currency'}} → {{change.newPrice | rf:'currency'}}</span>
            <span *ngIf="change.suspect" class="factor">{{priceChangeFactor(change)}}</span>
          </div>
          <div *ngIf="preview.priceChanges.length > 40" class="more-note">…and {{preview.priceChanges.length - 40}} more.</div>
        </div>

        <div *ngIf="preview?.unmatched?.length > 0" class="unmatched-block">
          <div class="block-title">Unmatched rows</div>
          <div *ngFor="let row of preview.unmatched | slice:0:20" class="unmatched-row">
            {{row.description || (row.identityValues || []).join(' / ') || row.productId}} <span *ngIf="row.qty != null">(qty {{row.qty}})</span>
          </div>
          <div *ngIf="preview.unmatched.length > 20" class="more-note">…and {{preview.unmatched.length - 20}} more.</div>
          <label *ngIf="isVendorInvoice()" class="create-unmatched">
            <input type="checkbox" [(ngModel)]="createUnmatched" />
            Create {{preview.unmatched.length}} vendor products for the unmatched rows and add their lines
          </label>
        </div>

        <div *ngIf="preview?.warnings?.length > 0" class="warnings-block">
          <div class="block-title">Warnings</div>
          <div *ngFor="let warning of preview.warnings | slice:0:20" class="warning-row">{{warning.msg}}</div>
        </div>

        <div *ngIf="preview?.errors?.length > 0" class="warnings-block">
          <div class="block-title">Errors</div>
          <div *ngFor="let error of preview.errors | slice:0:20" class="warning-row">{{error.msg}}</div>
        </div>

        <div *ngIf="!isVendorInvoice()" class="save-format">
          <div class="label">Save this mapping as a format (optional)</div>
          <input type="text" [(ngModel)]="saveFormatName" placeholder="e.g. Distributor Order Sheet" />
        </div>
      </div>

      <!-- ==== Replenish: parameters ==== -->
      <div *ngSwitchCase="'replenishParams'" class="replenish-params">
        <!-- The strategy choice only appears when the opt-in Stock Targets
             strategy is enabled for this client; otherwise days-of-supply is
             the only (and pre-selected) strategy. -->
        <div *ngIf="stockTargetsEnabled" class="strategy-tiles">
          <div class="mode-tile" [class.selected]="strategy === 'stock-targets'" (click)="strategy = 'stock-targets'">
            <div class="tile-name">Stock Targets</div>
            <div class="tile-description">Fill each item to its configured replenish level at the target location.</div>
          </div>
          <div class="mode-tile" [class.selected]="strategy === 'days-of-supply'" (click)="strategy = 'days-of-supply'">
            <div class="tile-name">Days of Supply</div>
            <div class="tile-description">Target days of supply from average sales, minus on-hand and inventory in transit to the target.</div>
          </div>
        </div>

        <div *ngIf="strategy === 'days-of-supply'" class="params-row">
          <div class="setting-inline">
            <div class="label">Sales window (days)</div>
            <input type="number" min="1" max="365" [(ngModel)]="windowDays" />
          </div>
          <div class="setting-inline">
            <div class="label">Target days of supply</div>
            <input type="number" min="1" max="365" [(ngModel)]="targetDays" />
          </div>
        </div>

        <!-- Category / subcategory limiter — discount-wizard-style drill-down.
             Nothing selected means every category is considered. -->
        <div *ngIf="scopeCategories?.length" class="limit-section">
          <div class="label">Limit to</div>
          <div class="scope-select">
            <div class="results">
              <div class="column-header">Categories</div>
              <div class="options">
                <div *ngFor="let category of scopeCategories" class="option scope-row"
                  [class.active]="activeScopeCategory?._id == category._id"
                  [class.selected]="isScopeSelected(category._id)"
                  (click)="activateScopeCategory(category)">
                  <span class="check" (click)="toggleScopeCategory(category); $event.stopPropagation()">{{isScopeSelected(category._id) ? '&#10003;' : '+'}}</span>
                  <span class="name">{{category.name}}</span>
                  <span *ngIf="category.subcategories?.length" class="sub-count">{{category.subcategories.length}} &#8250;</span>
                </div>
              </div>
            </div>
            <div class="results">
              <div class="column-header">
                Subcategories<span *ngIf="activeScopeCategory">({{activeScopeCategory.name}})</span>
              </div>
              <div class="options">
                <div *ngIf="!activeScopeCategory" class="empty-hint">Select a category on the left to browse its subcategories.</div>
                <div *ngIf="activeScopeCategory && !activeScopeCategory.subcategories?.length" class="empty-hint">No subcategories in {{activeScopeCategory.name}}.</div>
                <div *ngIf="activeScopeCategory && isScopeSelected(activeScopeCategory._id)" class="empty-hint">All of {{activeScopeCategory.name}} is selected, subcategories included.</div>
                <ng-container *ngIf="activeScopeCategory && !isScopeSelected(activeScopeCategory._id)">
                  <div *ngFor="let sub of activeScopeCategory.subcategories" class="option scope-row"
                    [class.selected]="isScopeSelected(sub._id)"
                    (click)="toggleScopeSubcategory(sub, activeScopeCategory)">
                    <span class="check">{{isScopeSelected(sub._id) ? '&#10003;' : '+'}}</span>
                    <span class="name">{{sub.name}}</span>
                  </div>
                </ng-container>
              </div>
            </div>
            <div class="selected-column">
              <div class="column-header">
                Selected<span>({{replenishScope.length || 0}})</span>
                <div *ngIf="replenishScope.length" class="action" (click)="clearScope()">Clear</div>
              </div>
              <div class="options">
                <div *ngIf="!replenishScope.length" class="empty-hint">Nothing selected — every category will be considered.</div>
                <div *ngFor="let entry of replenishScope" class="option scope-row" (click)="removeScope(entry)">
                  <span class="check remove">&times;</span>
                  <span class="name">{{entry.name}}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==== Replenish: preview ==== -->
      <div *ngSwitchCase="'replenishPreview'" class="replenish-preview">
        <div *ngIf="replenishMeta" class="summary-chips">
          <div class="chip good">{{replenishMeta.suggested | rf:'number'}} suggested</div>
          <div class="chip">{{replenishMeta.skippedNoNeed | rf:'number'}} no need</div>
          <div *ngIf="replenishMeta.skippedShortage > 0" class="chip warn">{{replenishMeta.skippedShortage}} short at source</div>
          <div *ngIf="replenishMeta.skippedUnlinked > 0" class="chip warn">{{replenishMeta.skippedUnlinked}} unlinked</div>
          <div *ngIf="replenishMeta.skippedComposite > 0" class="chip">{{replenishMeta.skippedComposite}} composite</div>
        </div>

        <div class="select-controls">
          <span class="edit-link" (click)="toggleAllSuggestions(true)">Select all</span>
          <span class="edit-link" (click)="toggleAllSuggestions(false)">Select none</span>
          <input class="suggestion-filter" type="text" placeholder="Filter items…" [(ngModel)]="suggestionFilter" />
          <span class="totals">{{selectedSuggestions().length | rf:'number'}} lines · {{selectedSuggestionQty() | rf:'number'}} total qty</span>
        </div>

        <div class="table-scroll tall">
          <table class="preview-table">
            <thead>
              <tr>
                <th></th>
                <th class="sortable" (click)="sortSuggestions('itemNumber')">Item #<span *ngIf="suggestionSortKey === 'itemNumber'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th class="sortable" (click)="sortSuggestions('name')">SKU/Item<span *ngIf="suggestionSortKey === 'name'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th class="sortable" (click)="sortSuggestions('categoryName')">Category<span *ngIf="suggestionSortKey === 'categoryName'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th class="sortable" (click)="sortSuggestions('subcategoryName')">Subcategory<span *ngIf="suggestionSortKey === 'subcategoryName'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th class="sortable num" (click)="sortSuggestions('avgDailySales')">Avg/day<span *ngIf="suggestionSortKey === 'avgDailySales'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th class="sortable num" (click)="sortSuggestions('onHandTarget')">On hand<span *ngIf="suggestionSortKey === 'onHandTarget'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th class="sortable num" (click)="sortSuggestions('inTransit')">In transit<span *ngIf="suggestionSortKey === 'inTransit'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th *ngIf="hasSourceColumn()" class="sortable num" (click)="sortSuggestions('onHandSource')">WH OH Qty<span *ngIf="suggestionSortKey === 'onHandSource'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th class="sortable num" (click)="sortSuggestions('qty')">{{qtyLabel()}}<span *ngIf="hasCaseColumn()"> (cases)</span><span *ngIf="suggestionSortKey === 'qty'" class="sort-arrow">{{suggestionSortDirection === 1 ? '▲' : '▼'}}</span></th>
                <th>Flags</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let suggestion of displaySuggestions()" [class.excluded]="!suggestion.include">
                <td class="check-cell"><input type="checkbox" [(ngModel)]="suggestion.include" /></td>
                <td class="item-number">{{suggestion.itemNumber}}</td>
                <td class="sku-name">
                  <div *ngIf="itemNumberPrimary() ? suggestion.itemNumber : suggestion.sku" class="sku">{{itemNumberPrimary() ? suggestion.itemNumber : suggestion.sku}}</div>
                  <div class="name">{{suggestion.name}}</div>
                  <div *ngIf="suggestion.unitOfMeasure || suggestion.unitDescription || suggestion.attr || suggestion.caseQty > 1"
                       class="units">
                    {{suggestion.unitOfMeasure}} {{suggestion.unitDescription}}<span *ngIf="suggestion.attr"> · {{suggestion.attr}}</span><span *ngIf="suggestion.caseQty > 1"> · ×{{suggestion.caseQty}}/case</span>
                  </div>
                </td>
                <td class="group-cell">{{suggestion.categoryName}}</td>
                <td class="group-cell">{{suggestion.subcategoryName}}</td>
                <td class="num">{{suggestion.avgDailySales}}</td>
                <td class="num">{{suggestion.onHandTarget | rf:'number'}}</td>
                <td class="num">{{suggestion.inTransit | rf:'number'}}</td>
                <td *ngIf="hasSourceColumn()" class="num">{{suggestion.onHandSource | rf:'number'}}</td>
                <td class="num qty-cell">
                  <input type="number" min="0" [(ngModel)]="suggestion.qty"
                    [class.increment-warning]="suggestion.orderIncrement > 1 && suggestion.qty > 0 && suggestion.qty % suggestion.orderIncrement !== 0"
                    [title]="(suggestion.orderIncrement > 1 && suggestion.qty > 0 && suggestion.qty % suggestion.orderIncrement !== 0) ? 'Not a multiple of the order increment (' + suggestion.orderIncrement + ')' : ''" />
                </td>
                <td class="flags"><span *ngFor="let label of suggestionFlagLabels(suggestion)" class="flag">{{label}}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ==== Add by Filter ==== -->
      <div *ngSwitchCase="'filter'" class="filter-step">
        <div class="msg">Add every matching inventory item to this transfer.</div>
        <div *ngIf="categories && categories.length > 0" class="setting-inline">
          <div class="label">Limit to</div>
          <select [(ngModel)]="selectedCategory">
            <option *ngFor="let category of categories" [ngValue]="category">{{category.subtype}} {{category.name}}</option>
          </select>
        </div>
      </div>

    </div>

    <div class="modal-buttons">
      <div *ngIf="step !== 'mode'" class="modal-button" (click)="back()">Back</div>
      <div *ngIf="step === 'mode'" class="modal-button" (click)="close()">Cancel</div>
      <div class="spacer"></div>
      <div *ngIf="step === 'mapping'" class="modal-button" [class.disabled]="!mappingComplete() || busy" (click)="mappingComplete() && !busy && applyMapping()">Preview</div>
      <div *ngIf="step === 'preview'" class="modal-button confirm" [class.disabled]="busy || !preview?.matchedCount" (click)="!busy && preview?.matchedCount && commitImport()">Import {{preview?.matchedCount}} Lines</div>
      <div *ngIf="step === 'replenishParams'" class="modal-button confirm" [class.disabled]="busy" (click)="!busy && runSuggestions()">Preview</div>
      <div *ngIf="step === 'replenishPreview'" class="modal-button confirm" [class.disabled]="busy || selectedSuggestions().length === 0" (click)="!busy && selectedSuggestions().length > 0 && commitReplenish()">Add {{selectedSuggestions().length}} Items</div>
      <div *ngIf="step === 'filter'" class="modal-button confirm" [class.disabled]="busy" (click)="!busy && addByFilter()">Add Items</div>
    </div>

    <div *ngIf="busy" class="busy-overlay">
      <svg class="busy-spinner" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path d="M1639 1056q0 5-1 7-64 268-268 434.5t-478 166.5q-146 0-282.5-55t-243.5-157l-129 129q-19 19-45 19t-45-19-19-45v-448q0-26 19-45t45-19h448q26 0 45 19t19 45-19 45l-137 137q71 66 161 102t187 36q134 0 250-65t186-179q11-17 53-117 8-23 30-23h192q13 0 22.5 9.5t9.5 22.5zm25-800v448q0 26-19 45t-45 19h-448q-26 0-45-19t-19-45 19-45l138-138q-148-137-349-137-134 0-250 65t-186 179q-11 17-53 117-8 23-30 23h-199q-13 0-22.5-9.5t-9.5-22.5v-7q65-268 270-434.5t480-166.5q146 0 284 55.5t245 156.5l130-129q19-19 45-19t45 19 19 45z"/></svg>
      <div class="busy-message">{{busyMessage}}</div>
    </div>
  </div>
</div>
